// Day-granularity date logic for the games date strip.
//
// Two different notions of "what day is it" meet here and must not be mixed:
//   • Season bounds are UTC-midnight timestamps that stand for a calendar day,
//     so their day is read off the UTC components.
//   • "Today" is the user's LOCAL calendar day.
// Both reduce to a "yyyy-MM-dd" key. Those are lexically sortable, so every
// comparison below is a plain string compare with no timezone arithmetic left
// to get wrong.

/** The calendar day a season bound stands for, read from its UTC components. */
export const seasonDayKey = (value: Date | string | null | undefined): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const key = value.slice(0, 10);
    return /^\d{4}-\d{2}-\d{2}$/.test(key) ? key : null;
  }
  if (Number.isNaN(value.getTime())) return null;
  return value.toISOString().slice(0, 10);
};

/** The user's local calendar day. */
export const localDayKey = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * A "yyyy-MM-dd" key as a local-midnight Date. Built from parts because
 * new Date("2026-06-21") parses as UTC and lands on the previous day in
 * westward timezones.
 */
export const dateFromDayKey = (key: string | null | undefined): Date | null => {
  if (!key) return null;
  const [y, m, d] = key.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const maxKey = (keys: string[]): string => keys.reduce((a, b) => (a > b ? a : b));
const minKey = (keys: string[]): string => keys.reduce((a, b) => (a < b ? a : b));

export type SnapInput = {
  /** Local calendar day key for today. */
  todayKey: string;
  /** Season bounds as day keys. null on a side means unbounded there. */
  startKey: string | null;
  endKey: string | null;
  /** Days with a PLAYED (recorded) game. undefined while the query is in flight. */
  playedKeys: string[] | undefined;
  /** Days with ANY game. undefined while the query is in flight. */
  allKeys: string[] | undefined;
};

/**
 * The day the date strip opens on when the season changes.
 *
 *   season underway, games already played → today
 *   season over                           → its last PLAYED day
 *   nothing played yet                    → the next day with a game,
 *                                           falling back to the last one
 *
 * Returns null when there is nothing to snap to, INCLUDING while either date
 * list is still loading. Callers must not latch on a null: the snap fires once
 * per season, so a day chosen mid-flight is the one the user would be stuck
 * with until they switch seasons.
 */
export const pickSnapDate = ({
  todayKey,
  startKey,
  endKey,
  playedKeys,
  allKeys,
}: SnapInput): Date | null => {
  if (!playedKeys) return null;

  // An absent bound means "no constraint on that side", matching the strip's
  // own in-bounds test: a season with no endDate is ongoing, not long finished.
  const started = !startKey || startKey <= todayKey;
  const ended = !!endKey && endKey < todayKey;

  // Today's games are `scheduled`/`live`, never `recorded`, so the last PLAYED
  // day is always yesterday-or-earlier while a season is underway. Opening on
  // it is what put the page a day behind the current slate.
  if (started && !ended && playedKeys.length) return dateFromDayKey(todayKey);

  if (playedKeys.length) return dateFromDayKey(maxKey(playedKeys));

  // Nothing played at all. A season reaches the dropdown on the strength of one
  // *available* game, which is not the same as a played one, so this is reached
  // three ways: the window has opened but the first game hasn't been played, the
  // season is still entirely ahead, or it is over but no game ever advanced to
  // `recorded`. Prefer the next game; fall back to the most recent one.
  if (!allKeys?.length) return null;
  const upcoming = allKeys.filter((k) => k >= todayKey);
  return dateFromDayKey(upcoming.length ? minKey(upcoming) : maxKey(allKeys));
};

export default pickSnapDate;
